import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { getApiError } from "../api/errors";
import { returnNativePosSaleItem } from "../api/nativePos";
import { getSale, getSales } from "../api/sales";
import { queueStoreBridgeSaleReceipt } from "../api/storeBridge";
import PageHeader from "../components/PageHeader";
import { EmptyPanel, ErrorPanel, LoadingPanel } from "../components/PageState";
import VariantSnapshotLabel from "../components/VariantSnapshotLabel";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { formatDate, formatMoney, titleCase } from "../utils/format";
import { getPrinterPaperWidth } from "../utils/hardwareReadiness";
import { printCanonicalSaleReceipt } from "../utils/receiptPrinter";
import { createStoreBridgeJobKey, getSelectedStoreBridgeDeviceId } from "../utils/storeBridge";

const PAGE_SIZE = 25;

function sourceLabel(source, t) {
  if (source === "native_pos") return t("sales.nativePos");
  if (source === "pos") return t("sales.importedPos");
  return source ? titleCase(source) : t("sales.unknown");
}

function statusLabel(status, t) {
  const keys = { completed: "completed", partially_refunded: "partiallyRefunded", refunded: "refunded", voided: "voided", paid: "paid", partial: "partial", pending: "pending" };
  return keys[status] ? t(`sales.${keys[status]}`) : titleCase(status);
}

function statusColor(status) {
  if (status === "completed") return "success";
  if (status === "partially_refunded") return "warning";
  if (status === "refunded") return "info";
  if (status === "voided") return "default";
  return "default";
}

function createReturnKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `native-pos-return-${crypto.randomUUID()}`;
  }
  return `native-pos-return-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function Sales() {
  const { t } = useTranslation();
  const { selectedWorkspace, selectedStore, canManage } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [returnLine, setReturnLine] = useState(null);
  const [returnQuantity, setReturnQuantity] = useState(1);
  const [returnReason, setReturnReason] = useState("");
  const [returnKey, setReturnKey] = useState(createReturnKey);
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [returnError, setReturnError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const load = useCallback(async () => {
    if (!selectedStore?.public_id) {
      setRows([]); setTotal(0); setLoading(false); return;
    }
    setLoading(true); setError("");
    try {
      const response = await getSales({ page, pageSize: PAGE_SIZE, query: query.trim(), source, status, paymentStatus, startDate, endDate });
      setRows(response.items || []); setTotal(Number(response.total || 0));
    } catch (requestError) {
      setError(getApiError(requestError, t("sales.loadError")));
    } finally { setLoading(false); }
  }, [endDate, page, paymentStatus, query, selectedStore?.public_id, source, startDate, status, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [endDate, paymentStatus, query, selectedStore?.public_id, source, startDate, status]);

  const openSale = async (publicId) => {
    setDetailLoading(true); setDetailError(""); setDetail(null);
    try { setDetail(await getSale(publicId)); }
    catch (requestError) { setDetailError(getApiError(requestError, t("sales.detailError"))); }
    finally { setDetailLoading(false); }
  };

  const refreshOpenSale = async (publicId) => {
    const fresh = await getSale(publicId);
    setDetail(fresh);
    return fresh;
  };

  const printSaleDetail = async () => {
    if (!detail) return;
    const bridgeDeviceId = getSelectedStoreBridgeDeviceId(
      selectedWorkspace?.public_id,
      selectedStore?.public_id,
    );
    if (bridgeDeviceId) {
      try {
        await queueStoreBridgeSaleReceipt(
          bridgeDeviceId,
          detail.public_id,
          createStoreBridgeJobKey("receipt-reprint"),
        );
        return;
      } catch (requestError) {
        setDetailError(getApiError(
          requestError,
          t("sales.directPrintError"),
        ));
      }
    }
    const paperWidthMm = getPrinterPaperWidth(
      selectedWorkspace?.public_id,
      selectedStore?.public_id,
    );
    printCanonicalSaleReceipt(detail, { storeName: selectedStore?.name, paperWidthMm });
  };

  const activeFilters = useMemo(() => Boolean(query || source || status || paymentStatus || startDate || endDate), [endDate, paymentStatus, query, source, startDate, status]);
  const clearFilters = () => { setQuery(""); setSource(""); setStatus(""); setPaymentStatus(""); setStartDate(""); setEndDate(""); };

  const openReturn = (line) => {
    setReturnLine(line); setReturnQuantity(1); setReturnReason(""); setReturnKey(createReturnKey()); setReturnError("");
  };
  const closeReturn = () => { if (!returnSubmitting) { setReturnLine(null); setReturnError(""); } };
  const changeReturnQuantity = (value) => { setReturnQuantity(Math.max(1, Number(value || 1))); setReturnKey(createReturnKey()); setReturnError(""); };
  const changeReturnReason = (value) => { setReturnReason(value); setReturnKey(createReturnKey()); setReturnError(""); };

  const submitReturn = async () => {
    if (!detail || !returnLine || returnSubmitting) return;
    const maxQuantity = Math.max(0, Math.floor(Number(returnLine.returnable_quantity || 0)));
    if (returnQuantity < 1 || returnQuantity > maxQuantity) {
      setReturnError(t("sales.returnRange", { max: maxQuantity })); return;
    }
    setReturnSubmitting(true); setReturnError("");
    try {
      await returnNativePosSaleItem(detail.public_id, {
        sale_line_id: returnLine.sale_line_id,
        quantity: returnQuantity,
        reason: returnReason.trim() || null,
      }, returnKey);
      const publicId = detail.public_id;
      setReturnLine(null);
      await Promise.all([refreshOpenSale(publicId), load()]);
    } catch (requestError) {
      setReturnError(getApiError(requestError, t("sales.returnError")));
    } finally { setReturnSubmitting(false); }
  };

  return (
    <MainLayout>
      <PageHeader eyebrow={t("sales.eyebrow")} title={t("nav.sales")} description={t("sales.subtitle", { store: selectedStore?.name || t("sales.thisStore") })} actions={<Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load}>{t("catalog.refresh")}</Button>} />
      <ErrorPanel message={error} onRetry={load} />
      <Card sx={{ borderRadius: 4, mb: 2.5 }}><CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Stack direction={{ xs: "column", lg: "row" }} gap={1.5} flexWrap="wrap" useFlexGap>
          <TextField size="small" label={t("sales.search")} value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("sales.searchPlaceholder")} sx={{ minWidth: { lg: 260 }, flex: 1 }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }} />
          <FormControl size="small" sx={{ minWidth: 160 }}><InputLabel>{t("sales.source")}</InputLabel><Select value={source} label={t("sales.source")} onChange={(event) => setSource(event.target.value)}><MenuItem value="">{t("sales.allSources")}</MenuItem><MenuItem value="native_pos">{t("sales.nativePos")}</MenuItem><MenuItem value="pos">{t("sales.importedPos")}</MenuItem></Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel>{t("sales.saleStatus")}</InputLabel><Select value={status} label={t("sales.saleStatus")} onChange={(event) => setStatus(event.target.value)}><MenuItem value="">{t("sales.allStatuses")}</MenuItem><MenuItem value="completed">{t("sales.completed")}</MenuItem><MenuItem value="partially_refunded">{t("sales.partiallyRefunded")}</MenuItem><MenuItem value="refunded">{t("sales.refunded")}</MenuItem><MenuItem value="voided">{t("sales.voided")}</MenuItem></Select></FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}><InputLabel>{t("sales.payment")}</InputLabel><Select value={paymentStatus} label={t("sales.payment")} onChange={(event) => setPaymentStatus(event.target.value)}><MenuItem value="">{t("sales.allPayments")}</MenuItem><MenuItem value="paid">{t("sales.paid")}</MenuItem><MenuItem value="partial">{t("sales.partial")}</MenuItem><MenuItem value="pending">{t("sales.pending")}</MenuItem><MenuItem value="refunded">{t("sales.refunded")}</MenuItem><MenuItem value="voided">{t("sales.voided")}</MenuItem></Select></FormControl>
          <TextField type="date" size="small" label={t("sales.from")} value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField type="date" size="small" label={t("sales.to")} value={endDate} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          {activeFilters && <Button onClick={clearFilters}>{t("common.clear")}</Button>}
        </Stack>
      </CardContent></Card>

      {loading ? <LoadingPanel label={t("sales.loading")} /> : rows.length === 0 ? <EmptyPanel icon="🧾" title={t("sales.noSales")} description={t("sales.noSalesHelp")} /> : (
        <Card sx={{ borderRadius: 4, overflow: "hidden" }}><TableContainer><Table size="small"><TableHead><TableRow><TableCell>{t("sales.invoice")}</TableCell><TableCell>{t("sales.source")}</TableCell><TableCell>{t("sales.sold")}</TableCell><TableCell>{t("sales.cashier")}</TableCell><TableCell>{t("common.status")}</TableCell><TableCell align="right">{t("sales.units")}</TableCell><TableCell align="right">{t("sales.refunded")}</TableCell><TableCell align="right">{t("common.total")}</TableCell></TableRow></TableHead><TableBody>
          {rows.map((sale) => <TableRow hover key={sale.public_id} onClick={() => openSale(sale.public_id)} sx={{ cursor: "pointer" }}>
            <TableCell><Typography fontWeight={850}>{sale.invoice_number || sale.external_sale_id}</Typography><Typography variant="caption" color="text.secondary">{t("sales.lines", { count: sale.item_count })}</Typography></TableCell>
            <TableCell><Chip size="small" label={sourceLabel(sale.source, t)} variant="outlined" /></TableCell><TableCell>{formatDate(sale.sold_at)}</TableCell><TableCell>{sale.cashier_name || "—"}</TableCell>
            <TableCell><Stack direction="row" gap={0.75} flexWrap="wrap" useFlexGap><Chip size="small" color={statusColor(sale.status)} label={statusLabel(sale.status, t)} /><Chip size="small" variant="outlined" label={statusLabel(sale.payment_status, t)} /></Stack></TableCell>
            <TableCell align="right">{Number(sale.unit_count || 0).toLocaleString()}</TableCell><TableCell align="right">{formatMoney(sale.refund_total, sale.currency)}</TableCell><TableCell align="right" sx={{ fontWeight: 850 }}>{formatMoney(sale.total_amount, sale.currency)}</TableCell>
          </TableRow>)}
        </TableBody></Table></TableContainer><Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" gap={1.5} sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{t("sales.salesCount", { count: total.toLocaleString("en-US") })}</Typography><Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} color="primary" /></Stack></Card>
      )}

      <Dialog open={detailLoading || Boolean(detail) || Boolean(detailError)} onClose={() => { setDetail(null); setDetailError(""); }} fullWidth maxWidth="md">
        <DialogTitle><Stack direction="row" gap={1} alignItems="center"><ReceiptLongRoundedIcon /><span>{detail?.invoice_number || t("sales.saleDetails")}</span></Stack></DialogTitle>
        <DialogContent dividers>
          {detailLoading ? <LoadingPanel label={t("sales.loadingDetail")} /> : null}{detailError ? <Alert severity="error">{detailError}</Alert> : null}
          {detail ? <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", sm: "row" }} gap={1} flexWrap="wrap" useFlexGap><Chip label={sourceLabel(detail.source, t)} variant="outlined" /><Chip label={statusLabel(detail.status, t)} color={statusColor(detail.status)} /><Chip label={t("sales.paymentStatus", { status: statusLabel(detail.payment_status, t) })} variant="outlined" /><Typography variant="body2" color="text.secondary" sx={{ alignSelf: "center" }}>{formatDate(detail.sold_at)}</Typography></Stack>
            <TableContainer><Table size="small"><TableHead><TableRow><TableCell>{t("sales.item")}</TableCell><TableCell align="right">{t("sales.sold")}</TableCell><TableCell align="right">{t("sales.returned")}</TableCell><TableCell align="right">{t("sales.returnable")}</TableCell><TableCell align="right">{t("common.price")}</TableCell><TableCell align="right">{t("common.total")}</TableCell><TableCell align="right">{t("sales.action")}</TableCell></TableRow></TableHead><TableBody>
              {(detail.items || []).map((item, index) => {
                const returnable = Number(item.returnable_quantity || 0);
                const canReturn = canManage && detail.source === "native_pos" && !["refunded", "voided"].includes(detail.status) && returnable > 0 && item.sale_line_id;
                return <TableRow key={`${item.sale_line_id || item.sku || item.product_name}-${index}`}>
                  <TableCell><VariantSnapshotLabel productName={item.product_name} variantLabel={item.variant_label} secondary={item.sku || t("sales.noSku")} /></TableCell>
                  <TableCell align="right">{Number(item.quantity).toLocaleString()}</TableCell><TableCell align="right">{Number(item.returned_quantity || 0).toLocaleString()}</TableCell><TableCell align="right">{returnable.toLocaleString()}</TableCell><TableCell align="right">{formatMoney(item.unit_price, detail.currency)}</TableCell><TableCell align="right">{formatMoney(item.line_total, detail.currency)}</TableCell>
                  <TableCell align="right">{canReturn ? <Button size="small" startIcon={<ReplayRoundedIcon />} onClick={() => openReturn(item)}>{t("sales.return")}</Button> : "—"}</TableCell>
                </TableRow>;
              })}
            </TableBody></Table></TableContainer>
            <Box><Typography fontWeight={900}>{t("sales.payments")}</Typography>{(detail.payments || []).map((payment, index) => <Typography key={`${payment.method}-${index}`} variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{payment.method === "cash" ? t("sales.cash") : titleCase(payment.method)} · {formatMoney(payment.amount, detail.currency)} · {statusLabel(payment.status, t)}</Typography>)}</Box>
            {(detail.returns || []).length > 0 && <Box><Typography fontWeight={900}>{t("sales.returns")}</Typography>{(detail.returns || []).map((returnRow, index) => <Box key={returnRow.return_id || index} sx={{ mt: 1, p: 1.25, borderRadius: 2, bgcolor: "action.hover" }}><Typography variant="body2" fontWeight={800}>{returnRow.product_name || t("sales.saleReturn")}{returnRow.variant_label ? ` · ${returnRow.variant_label}` : ""}{returnRow.sku ? ` · ${returnRow.sku}` : ""}</Typography><Typography variant="body2" color="text.secondary">{t("sales.quantityShort", { count: returnRow.quantity == null ? "—" : Number(returnRow.quantity).toLocaleString("en-US") })} · {formatMoney(returnRow.amount, detail.currency)} · {statusLabel(returnRow.status, t)} · {formatDate(returnRow.returned_at)}</Typography>{returnRow.reason ? <Typography variant="caption" color="text.secondary">{t("sales.reason", { value: returnRow.reason })}</Typography> : null}</Box>)}</Box>}
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "flex-end" }} gap={2}>
              <Box><Typography variant="caption" color="text.secondary">{t("sales.cashier")}</Typography><Typography fontWeight={800}>{detail.cashier_name || detail.employee_name || "—"}</Typography></Box>
              <Box textAlign={{ xs: "left", sm: "right" }}><Typography variant="body2" color="text.secondary">{t("sales.gross", { amount: formatMoney(detail.total_amount, detail.currency) })}</Typography><Typography variant="body2" color="text.secondary">{t("sales.refundedAmount", { amount: formatMoney(detail.refund_total, detail.currency) })}</Typography><Typography variant="caption" color="text.secondary">{t("sales.netSale")}</Typography><Typography variant="h5" fontWeight={950}>{formatMoney(detail.net_total, detail.currency)}</Typography></Box>
            </Stack>
          </Stack> : null}
        </DialogContent>
        <DialogActions><Button startIcon={<PrintRoundedIcon />} disabled={!detail} onClick={printSaleDetail}>{t("sales.printReceipt")}</Button><Button onClick={() => { setDetail(null); setDetailError(""); }}>{t("common.close")}</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(returnLine)} onClose={closeReturn} fullWidth maxWidth="sm">
        <DialogTitle>{t("sales.returnItem")}</DialogTitle><DialogContent dividers><Stack spacing={2}>
          {returnError ? <Alert severity="error">{returnError}</Alert> : null}
          <Alert severity="info">{t("sales.returnHelp")}</Alert>
          <Box><Typography fontWeight={900}>{returnLine?.product_name}</Typography>{returnLine?.variant_label && <Typography variant="body2" color="primary.main" fontWeight={750}>{returnLine.variant_label}</Typography>}<Typography variant="body2" color="text.secondary">{returnLine?.sku || t("sales.noSku")} · {t("sales.returnableUnits", { count: Number(returnLine?.returnable_quantity || 0).toLocaleString("en-US") })}</Typography></Box>
          <TextField label={t("common.quantity")} type="number" value={returnQuantity} onChange={(event) => changeReturnQuantity(event.target.value)} inputProps={{ min: 1, max: Math.max(1, Math.floor(Number(returnLine?.returnable_quantity || 1))), step: 1 }} fullWidth />
          <TextField label={t("sales.reasonOptional")} value={returnReason} onChange={(event) => changeReturnReason(event.target.value)} multiline minRows={2} inputProps={{ maxLength: 1000 }} fullWidth />
        </Stack></DialogContent><DialogActions><Button onClick={closeReturn} disabled={returnSubmitting}>{t("common.cancel")}</Button><Button variant="contained" color="warning" onClick={submitReturn} disabled={returnSubmitting || !returnLine}>{t(returnSubmitting ? "sales.returning" : "sales.completeReturn")}</Button></DialogActions>
      </Dialog>
    </MainLayout>
  );
}
